import { useRef, useState, useEffect } from 'react';
import { 
  FiShuffle,
  FiRotateCw,
  FiArrowUp,
  FiArrowDown,
  FiTrash2
 } from "react-icons/fi";

import { availableFonts } from './AddTextSelector';
import useCanvas from '../hooks/useCanvas';

const CanvasArea = ({ }) => {
  const {
    layers,
    setLayers,
    mirrorLayer,
    resizeLayer,
    removeLayer
  } = useCanvas();

  // Minimum scale factor applied to controls so they never shrink below a usable size
  const MIN_CONTROL_SCALE = 0.35;

  const updateControlScales = (layerId, newScale) => {
    const controlSelectors = [
      `#canvas-layer-${layerId} #remove-layer`,
      `#canvas-layer-${layerId} #rotate-layer`,
      `#canvas-layer-${layerId} #mirror-layer`,
      `#canvas-layer-${layerId} #up-layer`,
      `#canvas-layer-${layerId} #down-layer`,
      `#canvas-layer-${layerId} .cursor-resize`
    ];
    
    // Calculate the inverse scale but clamp it, so our controls do not become too small
    const inverseScale = Math.max(1 / newScale, MIN_CONTROL_SCALE);

    controlSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (selector.includes('cursor-resize')) {
          const transformOrigin = element.style.transformOrigin;
          element.style.transform = `scale(${inverseScale})`;
          element.style.transformOrigin = transformOrigin;
        } else {
          element.style.transform = `scale(${inverseScale})`;
        }
      });
    });
  };

  // Ensure controls are properly scaled when layers are added or modified (initial render, z-changes, etc.)
  useEffect(() => {
    layers.forEach(layer => {
      if (layer.type !== 'background') {
        updateControlScales(layer.id, layer.scale);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers]);

  const handleMoveStart = (e, layerId) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== 0) return;
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;
    const layerElement = document.getElementById(`canvas-layer-${layerId}`);
    if (!layerElement) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const currentLeft = parseInt(layerElement.style.left) || layer.x;
    const currentTop = parseInt(layerElement.style.top) || layer.y;
    
    layerElement.classList.add('moving');
    
    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      const currentY = e.clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      layerElement.style.left = `${currentLeft + deltaX}px`;
      layerElement.style.top = `${currentTop + deltaY}px`;
    };
    
    const handleMouseUp = () => {
      layerElement.classList.remove('moving');
      const finalLeft = parseInt(layerElement.style.left) || currentLeft;
      const finalTop = parseInt(layerElement.style.top) || currentTop;
      
      setLayers(prev => prev.map(l => 
        l.id === layerId 
          ? { ...l, x: finalLeft, y: finalTop }
          : l
      ));
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);    
  }

  const handleRotationStart = (e, layerId) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== 0) return;
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;
    const layerElement = document.getElementById(`canvas-layer-${layerId}`);
    if (!layerElement) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startRotation = layer.rotation || 0;
    layerElement.classList.add('rotating');
    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      const deltaX = currentX - startX;
      const rotationDelta = deltaX * 0.5; 
      const newRotation = (startRotation + rotationDelta) % 360;
      layerElement.style.transform = `rotate(${newRotation}deg) scale(${layer.scale})`;
    };
    const handleMouseUp = () => {
      layerElement.classList.remove('rotating');
      const transform = layerElement.style.transform;
      const rotationMatch = transform.match(/rotate\(([-\d.]+)deg\)/);
      let finalRotation = startRotation;
      if (rotationMatch && rotationMatch[1]) {
        finalRotation = parseFloat(rotationMatch[1]);
      }
      setLayers(prev => prev.map(layer => 
        layer.id === layerId 
          ? { ...layer, rotation: finalRotation }
          : layer
      ));
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  const handleResizeStart = (e, layerId, handle) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.button !== 0) return;
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;
    const layerElement = document.getElementById(`canvas-layer-${layerId}`);
    if (!layerElement) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startScale = layer.scale || 1;
    const startLeft = layer.x;
    const startTop = layer.y;
    
    layerElement.classList.add('resizing');
    
    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      const currentY = e.clientY;
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      
      let scaleDelta = 0;
      let newX = startLeft;
      let newY = startTop;
      
      // Determine appropriate sensitivity factor
      const sensitivity = layer.type === 'text' ? 0.006 : 0.002;

      switch (handle) {
        case 'br': // Bottom right
          scaleDelta = deltaX * sensitivity;
          break;
        case 'bl': // Bottom left
          scaleDelta = -deltaX * sensitivity;
          newX = startLeft + (layer.baseWidth * startScale - layer.baseWidth * Math.max(0.1, Math.min(3, startScale + scaleDelta))) / 2;
          break;
        case 'tr': // Top right
          scaleDelta = deltaX * sensitivity;
          newY = startTop + (layer.baseHeight * startScale - layer.baseHeight * Math.max(0.1, Math.min(3, startScale + scaleDelta))) / 2;
          break;
        case 'tl': // Top left
          scaleDelta = -deltaX * sensitivity;
          const scaleTemp = Math.max(0.1, Math.min(3, startScale + scaleDelta));
          newX = startLeft + (layer.baseWidth * startScale - layer.baseWidth * scaleTemp) / 2;
          newY = startTop + (layer.baseHeight * startScale - layer.baseHeight * scaleTemp) / 2;
          break;
        default:
          break;
      }
      
      const newScale = Math.max(0.1, Math.min(10, startScale + scaleDelta));
      
      layerElement.style.transform = `rotate(${layer.rotation}deg) scale(${newScale})`;
      layerElement.style.left = `${newX}px`;
      layerElement.style.top = `${newY}px`;
      updateControlScales(layerId, newScale);

      if (layer.type === 'text') {
        const textEl = layerElement.querySelector('p');
        if (textEl) {
          textEl.style.fontSize = `${100 * newScale}%`;
        }
      }
    };
    
    const handleMouseUp = () => {
      layerElement.classList.remove('resizing');
      const transform = layerElement.style.transform;
      const scaleMatch = transform.match(/scale\(([-\d.]+)\)/);
      let finalScale = startScale;
      if (scaleMatch && scaleMatch[1]) {
        finalScale = parseFloat(scaleMatch[1]);
      }
      const finalLeft = parseInt(layerElement.style.left) || startLeft;
      const finalTop = parseInt(layerElement.style.top) || startTop;
      
      resizeLayer(layerId, finalScale, finalLeft, finalTop);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleZChange = (e, layerId, direction) => {
    e.stopPropagation();
    e.preventDefault(); 
    if (e.button !== 0) return;
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;
    const layerIndex = layers.findIndex(l => l.id === layerId);
    const newZIndex = direction === 'down' ? layerIndex - 1 : layerIndex + 1;
    setLayers(prev => prev.map((l, index) => 
      index === layerIndex 
        ? { ...l, zIndex: newZIndex }
        : l
    ));
  };

  return (
    <div className="canvas-area w-full h-full flex items-center justify-center">
      <div 
        className="canvas-container" 
        style={{
          /* Keep the desktop design (800px square) but allow shrinking on narrow viewports */
          width: '100%',
          maxWidth: '800px',
          aspectRatio: '1 / 1', // maintain square shape while letting the width dictate the height
          position: 'relative',
          margin: '0 auto',
          border: '2px solid #ddd',
          backgroundColor: '#f9f9f9',
        }}
      >
        <div id="canvas-layers">
          {layers.map((layer, index) => (
            <div 
              key={index} 
              id={`canvas-layer-${layer.id}`}
              className="group"
              style={{
                position: 'absolute',
                top: `${layer.y}px`,
                left: `${layer.x}px`,
                width: layer.type === 'asset' ? '100%' : 'fit-content',
                height: layer.type === 'asset' ? '100%' : 'fit-content',
                zIndex: layer.zIndex,
                transform: `rotate(${layer.rotation}deg) scale(${layer.scale})`,
                transformOrigin: 'center center',
              }}
              onMouseDown={(e) => { handleMoveStart(e, layer.id) }}
            >
              <div className="w-full h-full absolute top-0 left-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div 
                  className="flex justify-center items-center"
                  style={{
                    transition: 'all 0.3s ease-in-out',
                    transform: `translate(0%, -200%)`,
                    transformOrigin: 'center center',
                    gap: `${32 / layer.scale}px`
                  }}
                >
                  <div 
                    id="rotate-layer" 
                    onMouseDown={(e) => { handleRotationStart(e, layer.id) }} 
                    className="flex justify-center items-center font-bold bg-[#f2a902] hover:brightness-90 active:brightness-110 cursor-pointer rounded-[100%] text-white p-1 aspect-square"
                  >
                    <FiRotateCw />
                  </div>
                  <div 
                    id="up-layer" 
                    onMouseDown={(e) => { e.stopPropagation(); handleZChange(e, layer.id, 'up') }} 
                    className="flex justify-center items-center font-bold bg-[#f2a902] hover:brightness-90 active:brightness-110 cursor-pointer rounded-[100%] text-white p-1 aspect-square"
                  >
                    <FiArrowUp />
                  </div>
                  <div 
                    id="down-layer" 
                    onMouseDown={(e) => { e.stopPropagation(); handleZChange(e, layer.id, 'down') }} 
                    className="flex justify-center items-center font-bold bg-[#f2a902] hover:brightness-90 active:brightness-110 cursor-pointer rounded-[100%] text-white p-1 aspect-square"
                  >
                    <FiArrowDown />
                  </div>
                  {layer.type !== 'text' && (
                    <div 
                      id="mirror-layer" 
                      onClick={(e) => { e.stopPropagation(); mirrorLayer(layer.id) }} 
                      className="flex justify-center items-center font-bold bg-[#f2a902] hover:brightness-90 active:brightness-110 cursor-pointer rounded-[100%] text-white p-1 aspect-square"
                    >
                      <FiShuffle />
                    </div>
                  )}
                  <div 
                    id="remove-layer" 
                    onClick={(e) => { e.stopPropagation(); removeLayer(layer.id) }} 
                    className="flex justify-center items-center font-bold bg-[#f2a902] hover:brightness-90 active:brightness-110 cursor-pointer rounded-[100%] text-white p-1 aspect-square"
                  >
                    <FiTrash2 />
                  </div>
                </div>
                {(() => {
                  // Calculate clamped scale for resize handles on initial render / move
                  const handleScale = Math.max(1 / layer.scale, MIN_CONTROL_SCALE);
                  return (
                    <div className="z-20 absolute cursor-resize top-[0%] left-[0%]" onMouseDown={(e) => handleResizeStart(e, layer.id, 'tl')} style={{ transform: `scale(${handleScale})`, transformOrigin: 'top left' }}>
                      <div className="w-4 aspect-square rounded-full bg-[#f2a90290] hover:brightness-90 active:brightness-110 translate-x-[-50%] translate-y-[-50%]" />
                    </div>
                  );
                })()}
                {(() => {
                  const handleScale = Math.max(1 / layer.scale, MIN_CONTROL_SCALE);
                  return (
                    <>
                      <div className="z-20 absolute cursor-resize top-[0%] right-[0%]" onMouseDown={(e) => handleResizeStart(e, layer.id, 'tr')} style={{ transform: `scale(${handleScale})`, transformOrigin: 'top right' }}>
                        <div className="w-4 aspect-square rounded-full bg-[#f2a90290] hover:brightness-90 active:brightness-110 translate-x-[50%] translate-y-[-50%]" />
                      </div>
                      <div className="z-20 absolute cursor-resize bottom-[0%] left-[0%]" onMouseDown={(e) => handleResizeStart(e, layer.id, 'bl')} style={{ transform: `scale(${handleScale})`, transformOrigin: 'bottom left' }}>
                        <div className="w-4 aspect-square rounded-full bg-[#f2a90290] hover:brightness-90 active:brightness-110 translate-x-[-50%] translate-y-[50%]" />
                      </div>
                      <div className="z-20 absolute cursor-resize bottom-[0%] right-[0%]" onMouseDown={(e) => handleResizeStart(e, layer.id, 'br')} style={{ transform: `scale(${handleScale})`, transformOrigin: 'bottom right' }}>
                        <div className="w-4 aspect-square rounded-full bg-[#f2a90290] hover:brightness-90 active:brightness-110 translate-x-[50%] translate-y-[50%]" />
                      </div>
                    </>
                  );
                })()}
              </div>
              {layer.type === 'asset' ? (
                <img 
                  draggable={false} 
                  src={layer.category === 'custom' ? layer.path : `./assets/${layer.path}`} 
                  alt={layer.name} 
                  className="z-20 object-contain w-full max-h-[100%] "
                  style={{
                    transform: layer.mirrored ? 'scaleX(-1)' : 'none'
                  }}
                />
              ) : (
                <div className="canvas-layer-content">
                  <p 
                    className="font-bold px-4 py-2"
                    style={{
                      fontFamily: availableFonts.find(f => f.name === layer.font)?.fontFamily,
                      fontSize: `${100 * layer.scale}%`,
                      color: layer.color,
                      textShadow: `
                        -1px -1px 0 ${layer.outlineColor},
                        1px -1px 0 ${layer.outlineColor},
                        -1px 1px 0 ${layer.outlineColor},
                        1px 1px 0 ${layer.outlineColor},
                        -2px 0 0 ${layer.outlineColor},
                        2px 0 0 ${layer.outlineColor},
                        0 -2px 0 ${layer.outlineColor},
                        0 2px 0 ${layer.outlineColor}
                      `
                    }}
                  >
                    {layer.text}
                  </p>
                </div>
              )}
              {/*layer.category === 'characters' && (
                <div className="bg-black/40 blur-xl bottom-0 left-0 right-0 absolute w-3/6 h-8 m-auto rounded-[100%] z-10"></div>
              )}*/}
            </div>
          ))}
        </div>
        <canvas 
          id="canvas"
          width={800} 
          height={800} 
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
          }}
        />
      </div>
    </div>
  );
};

export default CanvasArea;